import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/constant";
import DataTable from "react-data-table-component";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Modal from "../../components/modal";
import { customTableStyles } from "../../utils/tableStylings";

const ReportDetails = () => {
    const { id } = useParams(); // <-- get 31 from URL
    const location = useLocation();

    const query = new URLSearchParams(location.search);

    const [vendor, setVendor] = useState(null);
    const [data, setData] = useState(null);
    const [payments, setPaymentData] = useState(null);
    const [claimsData, setClaimData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showClaimModal, setShowClaimModal] = useState(false);
    const today = moment().format("YYYY-MM-DD");
    const firstDay = moment().startOf("month").format("YYYY-MM-DD");
    const initialStart = query.get("start_date") || firstDay;
    const initialEnd = query.get("end_date") || today;

    const [startDate, setStartDate] = useState(initialStart);
    const [endDate, setEndDate] = useState(initialEnd);

    const fetchDetails = async () => {
        try {
            const res = await axios.get(`${API_URL}/purchases/puchase-report/data/${id}?start_date=${startDate}&end_date=${endDate}`);
            console.log(res.data);
            setVendor(res.data.vendor);
            setData(res.data.data);
            setPaymentData(res.data.payments);
            setClaimData(res.data.claims)
        } catch (err) {
            setError("Failed to load report details");
        }
        setLoading(false);
    };

    const exportToExcel = () => {
        if (!formattedData || formattedData.length === 0) return;

        // Convert formattedData into flat rows for Excel
        const excelRows = formattedData.map(row => {
            const tax = Number(row?.data?.taxAmount || 0);
            const taxable = Number(row?.data?.taxableValue || 0);
            const gstPct = taxable ? `${Math.round((tax / taxable) * 100)}%` : "-";
            return {
                "Date": row.data.bill_date ? row.data.bill_date.split("T")[0] : "-",
                "Invoice No": row.data.bill_number || "-",
                "Amount": Number(row.data.totalAmount || 0),
                "Taxable Value": taxable,
                "Tax Amount": tax,
                "Total Pairs": row.data.totalPairs,
                "Payment Date": row.payment.payment_date ? row.payment.payment_date.split("T")[0] : "-",
                "Check No": row.payment.payment_details || "-",
                "Payment Amount": Number(row.payment.payment || 0),
                "GST Cost": gstPct,
            };
        });

        // ----- Calculate Totals -----
        const totalAmount = excelRows.reduce((sum, r) => sum + r["Amount"], 0);
        const totalTaxable = excelRows.reduce((sum, r) => sum + r["Taxable Value"], 0);
        const totalTax = excelRows.reduce((sum, r) => sum + r["Tax Amount"], 0);
        const totalPayment = excelRows.reduce((sum, r) => sum + r["Payment Amount"], 0);

        // ----- Add Totals Row -----
        excelRows.push({
            "Date": "",
            "Invoice No": "TOTAL",
            "Amount": totalAmount,
            "Taxable Value": totalTaxable,
            "Tax Amount": totalTax,
            "Total Pairs": "",
            "Payment Date": "",
            "Check No": "",
            "Payment Amount": totalPayment,
            "GST Cost": "",
        });

        // Convert to sheet
        const ws = XLSX.utils.json_to_sheet(excelRows);

        // Make TOTAL row bold (optional)
        const totalRowIndex = excelRows.length + 1; // +1 because Excel is 1-based
        Object.keys(excelRows[0]).forEach((key, idx) => {
            const cellRef = XLSX.utils.encode_cell({ r: totalRowIndex - 1, c: idx });
            if (!ws[cellRef]) return;
            ws[cellRef].s = {
                font: { bold: true },
            };
        });

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        // Download file
        XLSX.writeFile(wb, `Expense_Report_${moment().format("DD-MM-YYYY")}.xlsx`);
    };


    useEffect(() => {


        fetchDetails();
    }, [id]);

    const number_format = (number, decimals = 2) => {
        const n = Number(number);
        return n.toLocaleString("en-IN", {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    };

    const numberFormat = (num) => {
        return Number(num || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    // Calculate totals from formattedData instead of raw data


    const columns = [
        {
            name: "Date",
            selector: row => row.data.bill_date ? row.data.bill_date.split("T")[0] : "-",
            sortable: true
        },
        {
            name: "Invoice No",
            selector: row => row.data.bill_number || "-",
            sortable: true
        },
        {
            name: "Discount",
            selector: row => numberFormat(row.data.totalDisccount),
            sortable: true
        },
        {
            name: "Taxable Value",
            selector: row => numberFormat(row.data.taxableValue),
            sortable: true
        },

        {
            name: "Tax Amount",
            selector: row => numberFormat(row.data.taxAmount),
            sortable: true
        },

        {
            name: "Total Amount",
            selector: row => numberFormat(row.data.totalAmount),
            sortable: true
        },
        {
            name: "Returned Amount",
            selector: row => numberFormat(row.data.return_amount),
            sortable: true
        },

        {
            name: "Total Pairs",
            selector: row => row.data.totalPairs,
            sortable: true
        },

        {
            name: "Payment Date",
            selector: row => row.payment.payment_date
                ? row.payment.payment_date.split("T")[0]
                : "-",
            sortable: true
        },

        {
            name: "Check No",
            cell: row => (
                <div style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
                    {row?.payment?.payment_details || "-"}
                </div>
            ),
            sortable: true
        },
        {
            name: "Payment Amount",
            selector: row => numberFormat(row.payment.payment),
            sortable: true
        },

        {
            name: "GST Cost",
            selector: row => {
                const tax = Number(row?.data?.taxAmount || 0);
                const taxable = Number(row?.data?.taxableValue || 0);
                if (!taxable) return "-";
                const pct = Math.round((tax / taxable) * 100);
                return `${pct}%`;
            },
            sortable: true
        },
    ];


    // const columns = [
    //     { name: "Date", selector: row => row.bill_date.split("T")[0], sortable: true },
    //     { name: "Invoice No", selector: row => row.bill_number, sortable: true },

    //     {
    //         name: "Amount",
    //         selector: row => numberFormat(row.totalAmount),
    //         sortable: true
    //     },

    //     {
    //         name: "Taxable Value",
    //         selector: row => numberFormat(row.taxableValue),
    //         sortable: true
    //     },

    //     {
    //         name: "Tax Amount",
    //         selector: row => numberFormat(row.taxAmount),
    //         sortable: true
    //     },

    //     {
    //         name: "Total Pairs",
    //         selector: row => row.totalPairs,
    //         sortable: true
    //     },

    //     {
    //         name: "Payment Date",
    //         selector: row => row.payment_date || "-",
    //         sortable: true
    //     },

    //     {
    //         name: "Check No",
    //         selector: row => row.check_no || "-",
    //         sortable: true
    //     },

    //     {
    //         name: "Payment Amount",
    //         selector: row => numberFormat(row.payment_amount),
    //         sortable: true
    //     },

    //     {
    //         name: "GST Cost",
    //         selector: row => numberFormat(row.gstCost),
    //         sortable: true
    //     },
    // ];


    const customStyles = {
        headCells: {
            style: {
                backgroundColor: "#004da0ff",
                color: "white",
                fontWeight: "bold",
                fontSize: "14px",
            },
        },
    };

    const getTypeLabel = (type) => {
        return type === 'refund' ? 'Money Back' : 'Exchange';
    };

    const claimColumns = [
        {
            name: '#',
            cell: (row, index) => index + 1,
            width: '50px'
        },
        {
            name: 'Invoice No.',
            selector: row => row.SalesBill?.bill_no,
            sortable: true,
            width: '100px'
        },
        {
            name: 'Customer',
            cell: row => (
                <div style={{ display: 'block' }}>
                    <div><strong>{row.SalesBill?.Customer?.name || 'N/A'}</strong></div>
                    <div className="text-muted">{row.SalesBill?.Customer?.phone || '-'}</div>
                </div>
            )
        },
        {
            name: 'Product / Variant',
            cell: row => (
                <div className="d-flex flex-column gap-1">
                    {row.Variants?.length ? (
                        row.Variants.map(v => (
                            <div key={v.id} className='badge bg-primary'>
                                {/* Product name */}
                                <div className="fw-semibold">
                                    {v.FinalProduct?.name || 'N/A'}
                                </div>

                                {/* Variant badge */}
                                <div className="d-flex flex-wrap gap-1 mt-1">
                                    <span className="badge bg-white text-dark">
                                        {v.variant_name} &nbsp;
                                        {v.serial_no && (
                                            <span className="text-dark">
                                                ({v.serial_no})
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ))
                    ) : (
                        <span className="text-muted">-</span>
                    )}
                </div>
            )
        },
        {
            name: 'Type',
            cell: row => (
                <span className="badge bg-primary">
                    {getTypeLabel(row.type)}
                </span>
            ),
            width: '100px'
        },
        {
            name: 'Amount',
            selector: row => {
                if (row.type === 'refund') {
                    return `₹${Number(row.amount || 0).toFixed(2)}`;
                } else if (row.Variants?.length) {
                    const variantSum = row.Variants.reduce(
                        (sum, v) => sum + Number(v.price || 0),
                        0
                    );
                    return `₹${variantSum.toFixed(2)}`;
                } else {
                    return `₹0.00`;
                }
            },
            right: true,
            width: '120px'
        },
        {
            name: 'Date',
            cell: row => moment(row.createdAt).format('DD-MM-YYYY hh:mm A'),
            sortable: true,
            width: '180px'
        }
    ];


    const maxLength = Math.max(data?.length || 0, payments?.length || 0);

    // const formattedData = Array.from({ length: maxLength }).map((_, index) => {
    //     const row = data[index] || {};        // full purchase object
    //     const pay = payments[index] || {};    // full payment object

    //     const products = row.Products || [];

    //     // Compute totals
    //     const totalDisccount = products.reduce((sum, p) => sum + Number(p.discount || 0), 0);
    //     const totalAmount = products.reduce((sum, p) => sum + Number(p.total_amount || 0), 0);
    //     const taxableValue = products.reduce((sum, p) => sum + Number(p.price || 0), 0);
    //     const taxAmount = (totalAmount + totalDisccount) - taxableValue;
    //     const totalPairs = products.length;
    //     const gstCost = products.reduce(
    //         (sum, p) => sum + (Number(p.total_amount) - Number(p.price) || 0),
    //         0
    //     );

    //     return {
    //         data: {
    //             ...row,  
    //             totalDisccount,     // include ALL purchase fields
    //             totalAmount,
    //             taxableValue,
    //             taxAmount,
    //             totalPairs,
    //             gstCost
    //         },

    //         payment: {
    //             ...pay        // include ALL payment fields
    //         }
    //     };
    // });

    const formattedData = Array.from({ length: maxLength }).map((_, index) => {
        const row = data[index] || {};
        const pay = payments[index] || {};
        const products = row.Products || [];

        // 🔹 NORMAL PRODUCTS (exclude Return)
        const normalProducts = products.filter(
            p => p.item_type !== 'Return'
        );

        // 🔹 RETURN PRODUCTS
        const returnProducts = products.filter(
            p => p.item_type === 'Return'
        );

        // ---------------- NORMAL TOTALS ----------------
        const totalDisccount = row.product_discount !== undefined && row.product_discount !== null
            ? Number(row.product_discount || 0)
            : normalProducts.reduce(
                (sum, p) => sum + (Number(p.discount || 0) * Number(p.quantity || 1)),
                0
            );

        const totalAmount = row.total_amount !== undefined && row.total_amount !== null
            ? Number(row.total_amount || 0)
            : normalProducts.reduce(
                (sum, p) => sum + Number(p.total_amount || 0),
                0
            );

        const taxableValue = row.total_taxable_value !== undefined && row.total_taxable_value !== null
            ? Number(row.total_taxable_value || 0)
            : normalProducts.reduce(
                (sum, p) => sum + Number(p.price || 0),
                0
            );

        const taxAmount = row.gst !== undefined && row.gst !== null
            ? Number(row.gst || 0)
            : Number((totalAmount - taxableValue).toFixed(2));

        const totalPairs = normalProducts.reduce((sum, p) => sum + Number(p.quantity || 1), 0) || normalProducts.length;

        const gstCost = taxAmount;

        // ---------------- RETURN TOTAL ----------------
        const return_amount = returnProducts.reduce(
            (sum, p) => sum + Number(p.total_amount || 0),
            0
        );

        return {
            data: {
                ...row,
                totalDisccount,
                totalAmount,
                taxableValue,
                taxAmount,
                totalPairs,
                gstCost,
                return_amount, // ✅ NEW FIELD
            },

            payment: {
                ...pay,
            },
        };
    });


    const totalCount = formattedData?.length || 0;

    const totalAmount = formattedData?.reduce(
        (sum, row) => sum + Number(row?.data?.totalAmount || 0),
        0
    ) || 0;

    const totalDiscount = formattedData?.reduce(
        (sum, row) => sum + Number(row?.data?.totalDisccount || 0),
        0
    ) || 0;

    const totalTaxable = formattedData?.reduce((sum, row) => {
        let price = Number(row?.data?.taxableValue || 0);
        return sum + price;
    }, 0) || 0;

    const totalTax = formattedData?.reduce((sum, row) => {
        let tax = Number(row?.data?.taxAmount || 0);
        return sum + tax;
    }, 0) || 0;

    const totalPayment = formattedData?.reduce((sum, row) => {
        let tax = Number(row?.payment.payment || 0);
        return sum + tax;
    }, 0) || 0;

    const totalClaimAmount = claimsData?.reduce((sum, row) => {
        let amount = 0;

        if (row?.Variants?.length > 0) {
            row.Variants.forEach(v => {
                amount += Number(v.price || 0);
            });
        } else {
            amount += Number(row.amount || 0);
        }

        return sum + amount;
    }, 0) || 0;


    if (loading) return <div className="text-center p-4">Loading...</div>;
    if (error) return <div className="text-danger p-4">{error}</div>;

    const statsConfig = [
        {
            label: "Total Count",
            value: totalCount,
            bg: "#EEF2FF",
            color: "#4338CA"
        },
        {
            label: "Total Discount",
            value: number_format(totalDiscount),
            bg: "#ECFEFF",
            color: "#0369A1"
        },
        {
            label: "Total Taxable Value",
            value: number_format(totalTaxable),
            bg: "#FFF7ED",
            color: "#C2410C"
        },
        {
            label: "Total Tax",
            value: number_format(totalTax),
            bg: "#FEF2F2",
            color: "#B91C1C"
        },
        {
            label: "Total Amount",
            value: number_format(totalAmount),
            bg: "#F0FDF4",
            color: "#15803D"
        },
        {
            label: "Total Payment",
            value: number_format(totalPayment),
            bg: "#F0FDF4",
            color: "#9fdb05ff"
        }
    ];


    const claimCardConfig = {
        label: "Total Claim Amount",
        value: number_format(totalClaimAmount),
        bg: claimsData && claimsData.length > 0 ? "#FFF3CD" : "#F9FAFB",
        color: claimsData && claimsData.length > 0 ? "#856404" : "#6B7280"
    };


    const StatsCards = () => (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "20px",
                marginBottom: "15px",
                marginTop: "20px"
            }}
        >
            {statsConfig.map((item, index) => (
                <div
                    key={index}
                    style={{
                        ...cardStyle,
                        backgroundColor: item.bg,
                        borderLeft: `5px solid ${item.color}`
                    }}
                >
                    <div style={{ ...labelStyle, color: item.color }}>
                        {item.label}
                    </div>
                    <div style={{ ...valueStyle, color: item.color }}>
                        {item.value}
                    </div>
                </div>
            ))}

            <div
                style={{
                    ...cardStyle,
                    backgroundColor: claimCardConfig.bg,
                    borderLeft: `5px solid ${claimCardConfig.color}`
                }}
                onClick={showClaimModel}
                onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(0,0,0,0.15)")
                }
                onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow =
                    "0 2px 8px rgba(0,0,0,0.08)")
                }
            >
                <div style={{ ...labelStyle, color: claimCardConfig.color }}>
                    {claimCardConfig.label}
                </div>
                <div style={{ ...valueStyle, color: claimCardConfig.color }}>
                    {claimCardConfig.value}
                </div>
            </div>


        </div>
    );

    const cardStyle = {
        padding: "15px",
        background: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e5e7eb",
        cursor: "pointer",
        transition: "all 0.3s ease"
    };

    const claimCardStyle = {
        ...cardStyle,
    };

    const showClaimModel = () => {
        setShowClaimModal(true);
    };

    const labelStyle = {
        fontSize: "14px",
        color: "#6b7280",
        fontWeight: "500",
    };

    const valueStyle = {
        fontSize: "20px",
        fontWeight: "700",
        color: "#111827",
        marginTop: "5px",
    };


    return (
        <>
            <Modal
                show={showClaimModal}
                onClose={() => setShowClaimModal(false)}
                heading="Claim Details"
                size="xl"
            >
                <DataTable
                    columns={claimColumns}
                    data={claimsData || []}
                    pagination
                    highlightOnHover
                    customStyles={customTableStyles}
                    noDataComponent={<div className="p-3">No claims found</div>}
                />
            </Modal>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="row align-items-center g-2">
                                {/* Title */}
                                <div className="col-lg-5 col-md-12">
                                    <h3 className="mb-0 text-primary">
                                        {vendor?.name}
                                        <small className="text-muted d-block">
                                            (Showing:
                                            <strong>
                                                {" "}
                                                {moment(startDate).format("DD/MMM/YYYY")} -{" "}
                                                {moment(endDate).format("DD/MMM/YYYY")}
                                            </strong>
                                            )
                                        </small>
                                    </h3>
                                </div>

                                {/* Filters & Actions */}
                                <div className="col-lg-7 col-md-12">
                                    <div className="d-flex flex-wrap justify-content-lg-end align-items-end gap-2">

                                        {/* Export Button */}


                                        {/* Start Date */}
                                        <div className="position-relative">
                                            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                                Start Date
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control w-auto pt-3"
                                                value={moment(startDate).format("YYYY-MM-DD")}
                                                onChange={(e) => setStartDate(e.target.value)}
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div className="position-relative">
                                            <span className="badge bg-light text-muted position-absolute top-0 start-0 translate-middle-y px-2">
                                                End Date
                                            </span>
                                            <input
                                                type="date"
                                                className="form-control w-auto pt-3"
                                                value={moment(endDate).format("YYYY-MM-DD")}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                max={moment().format("YYYY-MM-DD")}
                                            />
                                        </div>

                                        {/* Apply */}
                                        <button
                                            className="btn btn-primary"
                                            onClick={fetchDetails}
                                        >
                                            Apply
                                        </button>

                                        {/* Reset */}
                                        <button
                                            className="btn btn-outline-danger"
                                            onClick={() => {
                                                setStartDate(firstDay);
                                                setEndDate(today);
                                                fetchDetails();
                                            }}
                                        >
                                            Reset
                                        </button>

                                        <button
                                            className="btn btn-success"
                                            onClick={exportToExcel}
                                        >
                                            Export Excel
                                        </button>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className="card-body">
                            <StatsCards />
                        </div>
                        <div className="card-body">
                            <DataTable
                                columns={columns}
                                data={formattedData}
                                progressPending={loading}
                                pagination
                                highlightOnHover
                                customStyles={customTableStyles}
                            />

                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ReportDetails;